import React, { useState, useEffect, useCallback } from 'react';
import { Snackbar, Alert, Box, Typography } from '@mui/material';
import QuizLayout from '../../../../shared/reusables/QuizLayout';
import MultipleChoiceCard from '../../../../shared/reusables/MultipleChoiceCard';
import QuizEndScreen from '../../../../shared/reusables/QuizEndScreen';
import { fetchWords } from '../../../../shared/api/darija';

const shuffleArray = (arr) => [...arr].sort(() => Math.random() - 0.5);

function buildOptions(data, correctItem, count = 4) {
    const distractors = shuffleArray(
        data.filter((v) => v.dutch !== correctItem.dutch)
    )
        .slice(0, count - 1)
        .map((v) => v.darijaBase);
    return shuffleArray([correctItem.darijaBase, ...distractors]);
}

/**
 * MultipleChoiceVerbs — MC quiz for verbs.
 * Kept separate because it uses "darijaBase" (not "darija").
 */
export default function MultipleChoiceVerbs({ onBack }) {
    const [data, setData] = useState([]);
    const [order, setOrder] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [selected, setSelected] = useState(null);
    const [options, setOptions] = useState([]);
    const [messages, setMessages] = useState([]);
    const [progress, setProgress] = useState([]);
    const [quizFinished, setQuizFinished] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    useEffect(() => {
        fetchWords('verbs')
            .then((verbs) => {
                const shuffled = shuffleArray(verbs.map((_, i) => i));
                setData(verbs);
                setOrder(shuffled);
                setProgress(shuffled.map(() => ({ correct: false })));
                if (verbs.length) setOptions(buildOptions(verbs, verbs[shuffled[0]]));
            })
            .catch((err) => {
                console.error('[MultipleChoiceVerbs]:', err);
                setError(err.message || 'Ophalen mislukt.');
            })
            .finally(() => setLoading(false));
    }, []);

    const currentVerb = order.length ? data[order[currentIndex]] : null;

    const addMessage = useCallback(
        (type, text) => setMessages((prev) => [...prev, { type, text, id: Date.now() }]),
        []
    );
    const handleCloseMessage = useCallback(
        (id) => setMessages((prev) => prev.filter((m) => m.id !== id)),
        []
    );

    const nextWord = useCallback(() => {
        const nextIndex = currentIndex + 1;
        if (nextIndex < order.length) {
            setCurrentIndex(nextIndex);
            setSelected(null);
            setOptions(buildOptions(data, data[order[nextIndex]]));
        } else {
            setQuizFinished(true);
        }
    }, [currentIndex, order, data]);

    const handleAnswer = useCallback(
        (option) => {
            if (selected || !currentVerb) return;
            setSelected(option);

            const isCorrect = option === currentVerb.darijaBase;
            setProgress((prev) => {
                const next = [...prev];
                next[currentIndex] = { correct: isCorrect };
                return next;
            });
            addMessage(
                isCorrect ? 'success' : 'error',
                isCorrect ? '✅ Goed gedaan!' : `❌ Fout! Het was: ${currentVerb.darijaBase}`
            );
            setTimeout(nextWord, 1000);
        },
        [selected, currentVerb, currentIndex, addMessage, nextWord]
    );

    const resetQuiz = useCallback(() => {
        const shuffled = shuffleArray(data.map((_, i) => i));
        setOrder(shuffled);
        setProgress(shuffled.map(() => ({ correct: false })));
        setCurrentIndex(0);
        setSelected(null);
        setQuizFinished(false);
        setMessages([]);
        if (data.length) setOptions(buildOptions(data, data[shuffled[0]]));
    }, [data]);

    if (loading) return null;

    if (error) {
        return (
            <Box sx={{ minHeight: '100vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                <Typography sx={{ color: '#f87171', fontFamily: "'DM Sans', sans-serif" }}>
                    ⚠️ {error}
                </Typography>
            </Box>
        );
    }

    if (quizFinished) {
        const correctCount = progress.filter((p) => p.correct).length;
        const wrongItems = progress
            .map((p, i) => ({ ...p, dutch: data[order[i]]?.dutch, answer: data[order[i]]?.darijaBase }))
            .filter((p) => !p.correct)
            .map((w) => ({ question: w.dutch, answer: w.answer }));
        return (
            <QuizEndScreen
                correct={correctCount}
                total={order.length}
                wrongItems={wrongItems}
                onRetry={resetQuiz}
            />
        );
    }

    if (!currentVerb) return null;

    return (
        <>
            <QuizLayout
                current={currentIndex + 1}
                total={order.length}
                label="Werkwoorden — MC"
                onBack={onBack}
            >
                <MultipleChoiceCard
                    question={`Wat is "${currentVerb.dutch}"?`}
                    accentWord={currentVerb.dutch}
                    questionIndex={currentIndex}
                    options={options}
                    correctAnswer={currentVerb.darijaBase}
                    selected={selected}
                    onAnswer={handleAnswer}
                    current={currentIndex + 1}
                    total={order.length}
                    label="Werkwoorden — MC"
                />
            </QuizLayout>

            {messages.map((msg) => (
                <Snackbar
                    key={msg.id}
                    open
                    autoHideDuration={1500}
                    onClose={() => handleCloseMessage(msg.id)}
                    anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                >
                    <Alert
                        onClose={() => handleCloseMessage(msg.id)}
                        severity={msg.type}
                        sx={{ width: '100%', minWidth: 250 }}
                    >
                        {msg.text}
                    </Alert>
                </Snackbar>
            ))}
        </>
    );
}
